/**
 * The detail half of the result-screen rating.
 *
 * DESIGN NOTES — this is a kiosk dialog, not a form.
 *
 * It renders as its own full-screen surface (scrim + light card) rather than
 * inheriting the page's theme. Five Result pages host it, on backgrounds that
 * range from a Bahela photo composite to a white default panel, and a survey
 * that half-disappears into someone's background image collects nothing. A
 * dialog is also the honest shape: it is asking for something, so it should
 * announce itself and then get out of the way.
 *
 * Every aspect shows its stars UP FRONT rather than behind a "select the
 * problem areas" step. A two-step flow (choose aspects → rate each) is the
 * obvious reading of "multiple choices, then a 1–5 scale for each", and it
 * doubles the taps for the guest who wants to answer one thing and leave. Here
 * the first tap IS the answer, and an aspect nobody touches is simply not
 * reported — "did not answer" and "rated this 1" are different facts.
 *
 * The strict flex-column shell (`shrink-0` header/footer, `min-h-0` scrolling
 * body) is deliberate and has bitten this codebase before: without it, on a
 * short landscape kiosk the submit button lands in an invisible scroll region
 * and the guest cannot finish — the same failure that once made the consent
 * dialog impossible to decline.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaStar, FaTimes } from 'react-icons/fa'
import { useDesignLayout } from '@renderer/hooks'
import { FEEDBACK_ASPECTS, isLowRating } from '@renderer/lib/feedback/aspects'

interface SessionFeedbackModalProps {
  isOpen: boolean
  /** The session rating that led here — picks the headline's tone. */
  rating: number | null
  answers: Record<string, number | null>
  onAnswer: (aspectKey: string, score: number) => void
  answeredCount: number
  onSubmit: () => void
  onClose: () => void
}

const SessionFeedbackModal: React.FC<SessionFeedbackModalProps> = ({
  isOpen,
  rating,
  answers,
  onAnswer,
  answeredCount,
  onSubmit,
  onClose
}) => {
  const { t } = useTranslation()
  const { isCompact, isSpacious } = useDesignLayout()

  if (!isOpen) return null

  const low = isLowRating(rating)

  const titleClass = isCompact ? 'text-lg' : isSpacious ? 'text-3xl' : 'text-2xl'
  const subtitleClass = isCompact ? 'text-xs' : isSpacious ? 'text-lg' : 'text-base'
  const labelClass = isCompact ? 'text-sm' : isSpacious ? 'text-xl' : 'text-lg'
  const hintClass = isCompact ? 'text-[11px]' : isSpacious ? 'text-sm' : 'text-xs'
  const iconClass = isCompact ? 'text-lg' : isSpacious ? 'text-2xl' : 'text-xl'
  // Kiosk touch targets: the session rating was once ~33px and audited as below
  // the comfortable tap floor. These stars are smaller than that one by design
  // (there are thirty of them) so the padding has to carry the target size.
  const starClass = isCompact ? 'p-1.5 text-lg' : isSpacious ? 'p-2.5 text-3xl' : 'p-2 text-2xl'
  const btnClass = isCompact
    ? 'px-4 py-2 text-sm'
    : isSpacious
      ? 'px-9 py-4 text-xl'
      : 'px-7 py-3 text-lg'

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t('result.feedback.title')}
    >
      <div className="flex max-h-[92dvh] w-full max-w-[min(94vw,880px)] flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl">
        {/* Header */}
        <div
          className={`relative shrink-0 border-b border-gray-100 text-center ${
            isCompact ? 'px-5 pb-3 pt-4' : 'px-8 pb-5 pt-7'
          }`}
        >
          <h2 className={`font-bold text-carbon ${titleClass}`}>
            {low ? t('result.feedback.titleLow') : t('result.feedback.title')}
          </h2>
          <p className={`mt-1 text-carbon/60 ${subtitleClass}`}>{t('result.feedback.subtitle')}</p>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('result.feedback.skip')}
            className={`absolute rounded-full p-2 text-carbon/40 transition-colors hover:bg-gray-100 hover:text-carbon/70 ${
              isCompact ? 'right-3 top-3' : 'right-5 top-5'
            }`}
          >
            <FaTimes />
          </button>
        </div>

        {/* Aspects — the only region allowed to scroll, so the footer stays put */}
        <div className={`min-h-0 flex-1 overflow-y-auto ${isCompact ? 'px-4 py-3' : 'px-7 py-5'}`}>
          <div className={`grid gap-3 ${isCompact ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {FEEDBACK_ASPECTS.map((aspect) => {
              const Icon = aspect.icon
              const score = answers[aspect.key] ?? null
              return (
                <div
                  key={aspect.key}
                  className={`rounded-2xl border-2 transition-colors ${
                    isCompact ? 'p-3' : 'p-4'
                  } ${score ? 'border-amber-300 bg-amber-50/60' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-start gap-2.5">
                    <Icon className={`mt-0.5 shrink-0 text-carbon/70 ${iconClass}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`truncate font-semibold text-carbon ${labelClass}`}>
                        {t(`result.feedback.aspects.${aspect.i18nKey}.label`)}
                      </p>
                      <p className={`truncate text-carbon/50 ${hintClass}`}>
                        {t(`result.feedback.aspects.${aspect.i18nKey}.hint`)}
                      </p>
                      {/* Stars sit in the text column, pulled left by the star
                          button's own padding, so each card has ONE left edge:
                          icon, label, hint and stars all line up. Centred rows
                          read as unmoored against a left-aligned label. */}
                      <div className={`mt-1.5 flex ${isCompact ? '-ml-1.5' : '-ml-2'}`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => onAnswer(aspect.key, star)}
                            className={`transition-transform active:scale-90 ${starClass}`}
                            aria-label={t('result.feedback.starAlt', {
                              rating: star,
                              aspect: t(`result.feedback.aspects.${aspect.i18nKey}.label`)
                            })}
                          >
                            <FaStar
                              className={`transition-colors duration-150 ${
                                score && star <= score ? 'text-amber-400' : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 ${
            isCompact ? 'px-4 py-3' : 'px-7 py-5'
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`rounded-full font-medium text-carbon/55 transition-colors hover:bg-gray-100 ${btnClass}`}
          >
            {t('result.feedback.skip')}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={answeredCount === 0}
            className={`rounded-full font-semibold text-white transition-colors ${btnClass} ${
              answeredCount === 0
                ? 'cursor-not-allowed bg-gray-300'
                : 'bg-carbon hover:bg-carbon/90'
            }`}
          >
            {answeredCount === 0
              ? t('result.feedback.submit')
              : t('result.feedback.submitCount', { count: answeredCount })}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * The way in for a guest who rated 3–5.
 *
 * Deliberately quiet — underlined text, no button chrome. A happy guest has
 * already given us their answer; this is an offer, and anything louder reads as
 * the booth not believing them.
 */
export const SessionFeedbackButton: React.FC<{
  onClick: () => void
  submitted: boolean
  className?: string
}> = ({ onClick, submitted, className = '' }) => {
  const { t } = useTranslation()
  if (submitted) return null
  return (
    <button
      type="button"
      onClick={onClick}
      className={`underline decoration-dotted underline-offset-4 opacity-70 transition-opacity hover:opacity-100 ${className}`}
    >
      {t('result.feedback.tellUsMore')}
    </button>
  )
}

export default SessionFeedbackModal
